import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Sidebar from '../components/Sidebar';
import api from '../utils/api';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Crown, Shield, Save, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';

const NUMERIC_LABELS = {
  max_pages: 'Pages',
  max_articles: 'Articles',
  max_newsletters: 'Newsletter sends / mo',
  max_subscribers: 'Subscribers',
  max_forms: 'Forms',
  max_form_submissions: 'Form submissions / mo',
  max_assets: 'Media files',
  max_storage_mb: 'Storage (MB)',
  max_team_members: 'Team members',
  max_landing_pages: 'Landing pages',
};
const BOOL_LABELS = {
  analytics: 'Analytics',
  api_access: 'API access',
  ai_features: 'AI features',
  custom_domain: 'Custom domain',
  remove_branding: 'Remove branding',
};

const fmtDefault = (v) => (v === -1 ? 'Unlimited' : v === undefined || v === null ? '—' : String(v));

const PlanLimits = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingPlan, setSavingPlan] = useState(null);
  // edits[plan_level] = { key: stringValue }  ('' means "use default")
  const [edits, setEdits] = useState({});

  const isMasterAdmin = user?.role === 'master_admin';

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/plan-limits');
      setData(res.data);
      const init = {};
      (res.data.plans || []).forEach((p) => {
        init[p.plan_level] = {};
        Object.keys(NUMERIC_LABELS).forEach((k) => {
          init[p.plan_level][k] = p.overrides && p.overrides[k] != null ? String(p.overrides[k]) : '';
        });
        Object.keys(BOOL_LABELS).forEach((k) => {
          init[p.plan_level][k] = p.overrides && p.overrides[k] != null ? (p.overrides[k] ? 'on' : 'off') : 'default';
        });
      });
      setEdits(init);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load plan limits');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (isMasterAdmin) load(); /* eslint-disable-next-line */ }, []);

  const setField = (plan, key, value) => {
    setEdits((prev) => ({ ...prev, [plan]: { ...prev[plan], [key]: value } }));
  };

  const savePlan = async (plan) => {
    setSavingPlan(plan);
    const overrides = {};
    Object.keys(NUMERIC_LABELS).forEach((k) => {
      const v = edits[plan][k];
      overrides[k] = v === '' || v == null ? null : parseInt(v, 10);
    });
    Object.keys(BOOL_LABELS).forEach((k) => {
      const v = edits[plan][k];
      overrides[k] = v === 'default' ? null : v === 'on';
    });
    try {
      await api.put(`/admin/plan-limits/${plan}`, { overrides });
      toast.success(`${plan} limits saved`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally {
      setSavingPlan(null);
    }
  };

  const resetPlan = async (plan) => {
    setSavingPlan(plan);
    try {
      await api.put(`/admin/plan-limits/${plan}`, { overrides: {} });
      toast.success(`${plan} reset to defaults`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to reset');
    } finally {
      setSavingPlan(null);
    }
  };

  if (!isMasterAdmin) {
    return (
      <div className="flex" data-testid="plan-limits-page">
        <Sidebar />
        <div className="flex-1 bg-slate-50 flex items-center justify-center h-screen">
          <Card className="w-96">
            <CardContent className="pt-6 text-center">
              <Shield className="w-12 h-12 mx-auto text-rose-500 mb-4" />
              <h2 className="text-xl font-semibold mb-2">Access Denied</h2>
              <p className="text-slate-500">Only Master Admins can configure plan limits.</p>
              <Button className="mt-4" onClick={() => navigate('/dashboard')}>Back to Dashboard</Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const plans = data?.plans || [];

  return (
    <div className="flex" data-testid="plan-limits-page">
      <Sidebar />
      <div className="flex-1 min-w-0 bg-slate-50 p-8 min-h-screen">
        <div className="max-w-5xl mx-auto">
          <div className="mb-8">
            <h1 className="text-4xl font-bold mb-2 flex items-center gap-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <Crown className="text-rose-500" size={32} />
              Plan Limits
            </h1>
            <p className="text-slate-600">
              Override the per-plan caps that gate Storage, Newsletter sends, Team size, Pages/Articles and feature access.
              Leave a field blank to use the built-in default. Use <code>-1</code> for unlimited.
            </p>
          </div>

          {loading ? (
            <div className="text-center py-20 text-slate-400">Loading plan limits…</div>
          ) : (
            <div className="space-y-6">
              {plans.map((p) => (
                <Card key={p.plan_level} data-testid={`plan-card-${p.plan_level}`}>
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between mb-4">
                      <h2 className="text-xl font-semibold capitalize">{p.display_name || p.plan_level}</h2>
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={() => resetPlan(p.plan_level)} disabled={savingPlan === p.plan_level} data-testid={`reset-${p.plan_level}`}>
                          <RotateCcw size={14} className="mr-1" /> Reset
                        </Button>
                        <Button size="sm" onClick={() => savePlan(p.plan_level)} disabled={savingPlan === p.plan_level} data-testid={`save-${p.plan_level}`}>
                          <Save size={14} className="mr-1" /> {savingPlan === p.plan_level ? 'Saving…' : 'Save'}
                        </Button>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                      {Object.entries(NUMERIC_LABELS).map(([key, label]) => (
                        <div key={key} className="space-y-1">
                          <Label className="text-xs text-slate-600">{label}</Label>
                          <Input
                            type="number"
                            value={edits[p.plan_level]?.[key] ?? ''}
                            onChange={(e) => setField(p.plan_level, key, e.target.value)}
                            placeholder={`Default: ${fmtDefault(p.defaults[key])}`}
                            className="h-8 text-sm"
                            data-testid={`limit-${p.plan_level}-${key}`}
                          />
                        </div>
                      ))}
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mt-4 pt-4 border-t border-slate-100">
                      {Object.entries(BOOL_LABELS).map(([key, label]) => (
                        <div key={key} className="space-y-1">
                          <Label className="text-xs text-slate-600">{label}</Label>
                          <Select value={edits[p.plan_level]?.[key] || 'default'} onValueChange={(v) => setField(p.plan_level, key, v)}>
                            <SelectTrigger className="h-8 text-sm" data-testid={`feature-${p.plan_level}-${key}`}><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="default">Default ({p.defaults[key] ? 'On' : 'Off'})</SelectItem>
                              <SelectItem value="on">Enabled</SelectItem>
                              <SelectItem value="off">Disabled</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PlanLimits;
